import React from 'react';

export interface UseInfiniteScrollProps {
  /**
   * Whether a request is currently loading or not.
   */
  loading: boolean;
  /**
   * The callback function to execute when the threshold is exceeded.
   */
  onLoadMore: () => void;
  /**
   * Maximum distance to the leading edge to trigger the callback. Default is 0.
   * For `"down"` direction this pads the bottom; for `"up"` direction this pads the top.
   */
  threshold?: number;
  /**
   * The scroll container for the IntersectionObserver root. Default is `"window"`.
   *
   * - `"window"` — use the viewport (null root).
   * - `"parent"` — use the sentinel's direct parentElement (must have overflow clipping).
   * - `React.RefObject<Element>` — use an explicit ancestor element; use this when the scroll
   *   container is neither the window nor the sentinel's direct parent.
   */
  scrollContainer?: 'window' | 'parent' | React.RefObject<Element>;
  /**
   * Direction the user scrolls to reach the sentinel. Default is `"down"`.
   * Use `"up"` when the sentinel is at the top of the list (e.g. loading older messages in a
   * chat). Reverses the direction guard and applies `threshold` to the top edge instead of
   * the bottom.
   *
   * NOTE: This value is expected to be static. The direction guard (`prevY`) is initialised
   * once at mount based on the initial `scrollDirection`. Changing this prop after mount will
   * update the rootMargin but not reset `prevY`, which may cause missed or spurious loads.
   */
  scrollDirection?: 'up' | 'down';
}

function useInfiniteScroll<T extends Element>({
  loading,
  onLoadMore,
  threshold = 0,
  scrollContainer = 'window',
  scrollDirection = 'down',
}: UseInfiniteScrollProps) {
  const sentinelRef = React.useRef<T>(null);

  // Initialised to the extreme in the scroll direction so the very first intersection
  // always fires regardless of starting position.
  //   down: MAX_SAFE_INTEGER — sentinel approaches with decreasing Y → prevY >= currentY ✓
  //   up:   MIN_SAFE_INTEGER — sentinel approaches with increasing Y → prevY <= currentY ✓
  const prevY = React.useRef(
    scrollDirection === 'up' ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER,
  );

  const callback = React.useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const approachingFromCorrectDirection =
          scrollDirection === 'up'
            ? // Scrolling up: sentinel Y increases as it comes into view from above
              prevY.current <= entry.boundingClientRect.y
            : // Scrolling down: sentinel Y decreases as it comes into view from below
              prevY.current >= entry.boundingClientRect.y;

        if (
          // is coming into viewport (i.e. it's not in the "leaving" phase)
          entry.isIntersecting &&
          // approached from the correct direction
          approachingFromCorrectDirection &&
          // we are not already loading more
          !loading
        ) {
          onLoadMore();
        }

        // Only update prevY when the sentinel is outside the viewport.
        // While it's visible we want to keep triggering loads until it scrolls out.
        if (!entry.intersectionRatio) {
          prevY.current = entry.boundingClientRect.y;
        }
      });
    },
    [loading, onLoadMore, scrollDirection],
  );

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    const sentinelNode = sentinelRef.current;
    if (sentinelNode) {
      // Resolve the IntersectionObserver root from the scrollContainer prop:
      //   ref object   — explicit ancestor element.
      //   'parent'     — sentinel's direct parentElement (must have overflow clipping).
      //   'window' / default — null (the viewport).
      let root: Element | null;
      if (scrollContainer !== 'window' && scrollContainer !== 'parent') {
        root = scrollContainer.current;
      } else if (scrollContainer === 'parent') {
        root = sentinelNode.parentElement;
      } else {
        root = null;
      }

      // For upward scroll the pre-load margin should extend above the root's top edge;
      // for downward scroll it extends below the root's bottom edge.
      const rootMargin =
        scrollDirection === 'up' ? `${threshold}px 0px 0px 0px` : `0px 0px ${threshold}px 0px`;

      const observer = new IntersectionObserver(callback, {
        root,
        threshold: 0,
        rootMargin,
      });
      observer.observe(sentinelNode);

      return () => observer.disconnect();
    }
    // FIXME: look into hook dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelRef.current, threshold, scrollContainer, scrollDirection, callback]);

  return { sentinelRef };
}

export default useInfiniteScroll;
